/**
 * Announce — transient flash for game-wide events. Owner: Phase-3 A3.2.
 *
 * Surfaces:
 *   - `twosRemoved` (RULES §2, PRD FR-G1): 4p jokers-on removes two of the
 *     four `2`s before the deal; the UI must announce *which* ones.
 *   - `roundForfeited` (RULES §6): joker-lead forfeit ended the round and
 *     awarded all remaining tricks to the opposing team.
 *
 * Both flash for ~3 s and then auto-dismiss. We watch `useRecentEvents()` for
 * a never-before-seen event id (the store's monotonic `seq`) and replace the
 * current banner with the newer one. `gameOver` is handled by A3.1 (game
 * summary screen), not by this component.
 *
 * The component is a polite live region so screen readers receive the
 * announcement without interrupting input.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import type { Card as ProtoCard, GameEventWire, Suit } from "@hookum/protocol";
import { useRecentEvents } from "../state/hooks.js";

const FLASH_MS = 3_000;

const SUIT_GLYPH: Record<Suit, string> = {
  C: "♣",
  D: "♦",
  H: "♥",
  S: "♠",
};

interface Banner {
  seq: number;
  kind: "twosRemoved" | "roundForfeited";
  title: string;
  detail: string;
}

function formatCard(c: ProtoCard): string {
  if (c.kind === "joker") return c.color === "RED" ? "Red Joker" : "Black Joker";
  const rank =
    c.rank === 11 ? "J" : c.rank === 12 ? "Q" : c.rank === 13 ? "K" : c.rank === 14 ? "A" : String(c.rank);
  return `${rank}${SUIT_GLYPH[c.suit]}`;
}

function bannerFor(
  entry: { seq: number; event: GameEventWire },
): Banner | null {
  const { event, seq } = entry;
  if (event.type === "twosRemoved") {
    const list = event.cards.map(formatCard).join(" and ");
    return {
      seq,
      kind: "twosRemoved",
      title: "Two cards removed",
      detail: `${list} are out this game`,
    };
  }
  if (event.type === "roundForfeited") {
    const tricks = event.count === 1 ? "1 hand" : `${event.count} hands`;
    return {
      seq,
      kind: "roundForfeited",
      title: "Round forfeited",
      detail: `Joker stranded with seat ${event.strandedSeat} — ${tricks} to the opposing team.`,
    };
  }
  return null;
}

function findLatestBanner(
  events: ReadonlyArray<{ seq: number; event: GameEventWire }>,
  sinceSeq: number,
): Banner | null {
  for (let i = events.length - 1; i >= 0; i--) {
    const e = events[i];
    if (!e || e.seq <= sinceSeq) continue;
    const b = bannerFor(e);
    if (b) return b;
  }
  return null;
}

export function Announce(): JSX.Element | null {
  const events = useRecentEvents();
  const [banner, setBanner] = useState<Banner | null>(null);
  const lastSeenSeq = useRef<number>(0);

  const latest = useMemo(
    () => findLatestBanner(events, lastSeenSeq.current),
    [events],
  );

  useEffect(() => {
    if (!latest) return;
    if (latest.seq <= lastSeenSeq.current) return;
    lastSeenSeq.current = latest.seq;
    setBanner(latest);
    const id = window.setTimeout(() => {
      setBanner((cur) => (cur && cur.seq === latest.seq ? null : cur));
    }, FLASH_MS);
    return () => window.clearTimeout(id);
  }, [latest]);

  if (!banner) return null;
  return (
    <div
      className={`announce announce-${banner.kind}`}
      role="status"
      aria-live="assertive"
      aria-atomic="true"
    >
      <strong className="announce-title">{banner.title}</strong>
      <span className="announce-detail">{banner.detail}</span>
    </div>
  );
}
